import { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Typography } from '@uniquegood/realworld-studio-design';
import { useDynamicMutation, useModalState } from '@/hooks';
import ExclamationModalContent from '@/components/ExclamationModalContent';

const EXPIRATION_NOTICE_TYPES: ReadonlyArray<ExpirationNoticeType> = [
  'ProPaymentFailed',
  'ClassAndTeacherExpirationNotice',
  'ProAndEnterpriseExpirationNotice',
  'IrregularPaymentPlanExpirationNotice'
];

const DOWNGRADE_TYPES: ReadonlyArray<DowngradeType> = [
  'IrregularPaymentPlanDowngrade',
  'ProPaymentCancelRequestDowngrade',
  'ProPaymentFailedDowngrade'
];

interface UseDowngradeProps {
  onOpenPromotionModal: () => void;
}

export default function useDowngrade({ onOpenPromotionModal }: UseDowngradeProps) {
  const { appId } = useParams<AppParam>();
  const { mutateByPath } =
    useDynamicMutation<CommonResponseModel<ApplicationNotificationResponseModel>>();
  const { modal, openModal, closeModal } = useModalState();
  const history = useHistory();

  const openNotificationModal = useCallback(
    (type: ExpirationNoticeType, title: string, body: string) => {
      if (type === 'ProPaymentFailed') {
        openModal({
          title,
          children: <ExclamationModalContent description={body} />,
          primaryAction: {
            content: '요금제 관리로 이동',
            onAction: () => {
              closeModal();
              history.push(`/apps/${appId}?tab=settings&subTab=plan`);
            }
          }
        });
      } else {
        openModal({
          title,
          children: <ExclamationModalContent description={body} />,
          primaryAction: {
            content: '1:1 문의하기',
            onAction: () => {
              closeModal();
              if (ChannelIO) {
                ChannelIO('showMessenger');
              }
            }
          }
        });
      }
    },
    []
  );

  const openCompleteDowngradeModal = useCallback((title: string, body: string) => {
    openModal({
      title,
      children: (
        <Typography as="p" type="body">
          {body}
        </Typography>
      ),
      primaryAction: {
        content: '확인',
        onAction: closeModal
      },
      secondaryAction: {
        content: '요금제 보러가기',
        onAction: () => {
          closeModal();
          onOpenPromotionModal();
        }
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await mutateByPath(`/apps/${appId}/notifications/consume`);

        if (!response?.data || !response.data.type || !response.data.title || !response.data.body) {
          return;
        }

        const { type, title, body } = response.data;

        if (EXPIRATION_NOTICE_TYPES.includes(type as ExpirationNoticeType)) {
          openNotificationModal(type as ExpirationNoticeType, title, body);
        } else if (DOWNGRADE_TYPES.includes(type as DowngradeType)) {
          openCompleteDowngradeModal(title, body);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [appId, openNotificationModal, openCompleteDowngradeModal]);

  return {
    downgradeModal: modal
  };
}
